import React, { useState } from "react";
import { Box, Image, Modal, Group, Button, Stack } from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import { IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";
import { Heading3, DimmedText } from "./Typography";

interface Photo {
  id: string | number;
  url: string;
  title?: string;
  description?: string;
  alt?: string;
}

interface PhotoShowcaseProps {
  photos: Photo[];
  title?: string;
  columns?: number;
  gap?: "xs" | "sm" | "md" | "lg" | "xl";
}

/**
 * PhotoShowcase - Component for displaying photos in a grid with lightbox
 * Usage: <PhotoShowcase photos={photos} title="Photoshoot 2024" />
 */
export const PhotoShowcase: React.FC<PhotoShowcaseProps> = ({
  photos,
  title,
  columns = 3,
  gap = "md",
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState<number | null>(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const openModal = (index: number) => {
    setSelectedPhoto(index);
    setCarouselIndex(index);
  };

  const closeModal = () => {
    setSelectedPhoto(null);
  };

  const nextPhoto = () => {
    if (selectedPhoto !== null) {
      const next = (selectedPhoto + 1) % photos.length;
      setSelectedPhoto(next);
      setCarouselIndex(next);
    }
  };

  const prevPhoto = () => {
    if (selectedPhoto !== null) {
      const prev = (selectedPhoto - 1 + photos.length) % photos.length;
      setSelectedPhoto(prev);
      setCarouselIndex(prev);
    }
  };

  return (
    <Box>
      {title && (
        <Stack gap="sm" mb="xl">
          <Heading3>{title}</Heading3>
          <DimmedText size="sm">{photos.length} photos</DimmedText>
        </Stack>
      )}

      <Box
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: `var(--mantine-spacing-${gap})`,
        }}
      >
        {photos.map((photo, index) => (
          <Box
            key={photo.id}
            style={{
              position: "relative",
              aspectRatio: "1",
              overflow: "hidden",
              borderRadius: "var(--mantine-radius-md)",
              cursor: "pointer",
              transition: "transform 0.3s ease",
            }}
            onClick={() => openModal(index)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <Image
              src={photo.url}
              alt={photo.alt || photo.title || `Photo ${index + 1}`}
              fit="cover"
              style={{
                width: "100%",
                height: "100%",
              }}
            />
            {photo.title && (
              <Box
                style={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background:
                    "linear-gradient(to top, rgba(0,0,0,0.8), transparent)",
                  padding: "1rem",
                  color: "white",
                }}
              >
                <Heading3 style={{ fontSize: "1rem", margin: 0 }}>
                  {photo.title}
                </Heading3>
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Modal
        opened={selectedPhoto !== null}
        onClose={closeModal}
        size="xl"
        centered
        withCloseButton={false}
        styles={{
          content: {
            backgroundColor: "rgba(26, 27, 30, 0.98)",
            border: "1px solid rgba(55, 58, 64, 0.4)",
          },
          body: {
            padding: 0,
          },
        }}
      >
        {selectedPhoto !== null && (
          <Box style={{ position: "relative" }}>
            <Carousel
              initialSlide={carouselIndex}
              onSlideChange={setCarouselIndex}
              withIndicators
              withKeyboardEvents
              styles={{
                indicator: {
                  backgroundColor: "rgba(249, 115, 22, 0.5)",
                  "&[data-active]": {
                    backgroundColor: "#f97316",
                  },
                },
              }}
            >
              {photos.map((photo) => (
                <Carousel.Slide key={photo.id}>
                  <Image
                    src={photo.url}
                    alt={photo.alt || photo.title || "Photo"}
                    fit="contain"
                    style={{
                      maxHeight: "70vh",
                      width: "100%",
                    }}
                  />
                </Carousel.Slide>
              ))}
            </Carousel>

            <Group
              justify="space-between"
              style={{
                position: "absolute",
                top: "1rem",
                left: "1rem",
                right: "1rem",
                zIndex: 10,
              }}
            >
              <Button
                variant="filled"
                color="orange"
                onClick={prevPhoto}
                leftSection={<IconChevronLeft size={18} />}
              >
                Previous
              </Button>
              <Button
                variant="filled"
                color="orange"
                onClick={closeModal}
                leftSection={<IconX size={18} />}
              >
                Close
              </Button>
              <Button
                variant="filled"
                color="orange"
                onClick={nextPhoto}
                rightSection={<IconChevronRight size={18} />}
              >
                Next
              </Button>
            </Group>

            {photos[selectedPhoto]?.title && (
              <Box
                style={{
                  position: "absolute",
                  bottom: "1rem",
                  left: "1rem",
                  right: "1rem",
                  background: "rgba(0, 0, 0, 0.7)",
                  padding: "1rem",
                  borderRadius: "var(--mantine-radius-md)",
                }}
              >
                <Heading3 style={{ fontSize: "1.25rem", margin: 0, color: "white" }}>
                  {photos[selectedPhoto].title}
                </Heading3>
                {photos[selectedPhoto].description && (
                  <DimmedText size="sm" style={{ marginTop: "0.5rem" }}>
                    {photos[selectedPhoto].description}
                  </DimmedText>
                )}
              </Box>
            )}
          </Box>
        )}
      </Modal>
    </Box>
  );
};

export default PhotoShowcase;

